/**
 * ServicesSection - What We Build
 *
 * Showcases the four core service offerings:
 * 1. Custom ERP Systems
 * 2. Consumer-Facing Applications
 * 3. Internal Automation & Systems
 * 4. Data Intelligence Dashboards
 */

import { SectionHeading, Muted } from "@/components/ui/section-heading";

const ServicesSection = () => {
  return (
    <section
      id="services"
      className="relative bg-white/90 py-16 sm:py-24 lg:py-32 overflow-hidden shadow-2xl rounded-3xl"
    >
      {/* CONTENT CONTAINER */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
        {/* SECTION HEADER */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <SectionHeading
            variant="question"
            className="mb-4 sm:mb-6 text-center"
          >
            What We Build
          </SectionHeading>
          <SectionHeading
            variant="headline"
            className="mb-8 sm:mb-12 lg:mb-16 text-center"
          >
            Custom software solutions that transform operations
            <br />
            and <Muted>drive growth.</Muted>
          </SectionHeading>
        </div>

        {/* FOUR-SERVICE GRID */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* SERVICE 1: CUSTOM ERP SYSTEMS */}
          <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-lg border-2 border-slate-200">
            <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                />
              </svg>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Custom ERP Systems
            </h3>

            <p className="text-slate-700 leading-relaxed mb-6">
              Tailored enterprise resource planning systems that unify your
              operations, eliminate data silos, and scale with your business
              growth.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Unified operations
                  </p>
                  <p className="text-sm text-slate-600">
                    Single source of truth across departments
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Process automation
                  </p>
                  <p className="text-sm text-slate-600">
                    Eliminate manual workflows and human error
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Scalable architecture
                  </p>
                  <p className="text-sm text-slate-600">
                    Grow without system limitations
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SERVICE 2: CONSUMER-FACING APPLICATIONS */}
          <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-lg border-2 border-slate-200">
            <div className="w-16 h-16 rounded-xl bg-purple-100 flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Consumer-Facing Applications
            </h3>

            <p className="text-slate-700 leading-relaxed mb-6">
              Beautiful, intuitive web and mobile applications that engage
              users, drive conversions, and deliver exceptional customer
              experiences.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Responsive design
                  </p>
                  <p className="text-sm text-slate-600">
                    Flawless experience on any device
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">Cross-platform</p>
                  <p className="text-sm text-slate-600">
                    Web, iOS, and Android from one codebase
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Performance optimized
                  </p>
                  <p className="text-sm text-slate-600">
                    Lightning-fast load times and interactions
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SERVICE 3: INTERNAL AUTOMATION & SYSTEMS */}
          <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-lg border-2 border-slate-200">
            <div className="w-16 h-16 rounded-xl bg-green-100 flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Internal Automation & Systems
            </h3>

            <p className="text-slate-700 leading-relaxed mb-6">
              Intelligent automation that eliminates manual tasks, reduces
              errors, and empowers your team to focus on high-value strategic
              work.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Workflow automation
                  </p>
                  <p className="text-sm text-slate-600">
                    Automate repetitive business processes
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    API integrations
                  </p>
                  <p className="text-sm text-slate-600">
                    Connect disparate systems seamlessly
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Custom triggers
                  </p>
                  <p className="text-sm text-slate-600">
                    Event-driven automation with intelligent rules
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SERVICE 4: DATA INTELLIGENCE DASHBOARDS */}
          <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-lg border-2 border-slate-200">
            <div className="w-16 h-16 rounded-xl bg-orange-100 flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Data Intelligence Dashboards
            </h3>

            <p className="text-slate-700 leading-relaxed mb-6">
              Transform raw data into actionable insights with intelligent
              aggregation, real-time analytics, and user-friendly visualization
              dashboards.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Real-time analytics
                  </p>
                  <p className="text-sm text-slate-600">
                    Live data streaming and instant insights
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Custom reporting
                  </p>
                  <p className="text-sm text-slate-600">
                    Build exactly the reports you need
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-slate-900">
                    Predictive insights
                  </p>
                  <p className="text-sm text-slate-600">
                    AI-powered forecasting and recommendations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA SECTION */}
        <div className="mt-8 sm:mt-12 lg:mt-16 text-center">
          <p className="text-lg text-slate-600 mb-6">
            Ready to transform your operations with custom software?
          </p>
          <a
            href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-[#B87333] text-white font-semibold rounded-xl hover:bg-[#A66329] transition-colors shadow-lg"
          >
            Get Started
            <svg
              className="w-5 h-5 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
